import { MarketNames } from '@src/components/MarketNames';
import { mapSyntheticProductTypeToLabel } from '@src/components/OMS/NewOrder/utils/mappers';
import type { Security } from '@talos/kyoko';
import {
  AggregationIcon,
  Box,
  Expiration,
  formattedUTCDate,
  HStack,
  Icon,
  IconName,
  isCompound,
  isETF,
  isFuture,
  isOption,
  isPerpetualSwap,
  isSpot,
  type MarketAccount,
  ProductTypeEnum,
  Text,
  Tooltip,
  UnifiedLiquidityEnum,
  useMarketAccountsContext,
  useOverflowHandler,
  useSecuritiesContext,
} from '@talos/kyoko';
import { uniq } from 'lodash-es';
import { memo } from 'react';
import styled from 'styled-components';
import { Title } from './styles';
import { OptionTitle } from './SymbolSubhead.OptionTitle';
import { PerpTitle } from './SymbolSubhead.PerpTitle';
import type { SymbolDisplayProps } from './types';

export const SYMBOL_DISPLAY_SUBHEAD_TEST_ID = 'symbol-display-subhead';
const SymbolDisplaySubheadWrapper = ({
  children,
  tooltipOnOverflow,
  $preserveCase,
  ...props
}: React.PropsWithChildren<{
  tooltipOnOverflow?: boolean;
  /** When true, subhead text keeps mixed case (e.g. empty-market warning). */
  $preserveCase?: boolean;
}>) => {
  const skip = !children;
  const { isOverflowing, ref } = useOverflowHandler({ skip });
  const titleCaseProps = { $preserveCase, ...props };
  return skip ? null : tooltipOnOverflow ? (
    <Box cursor="default">
      <Tooltip
        delay={500}
        tooltip={
          <Title allowWrapping {...titleCaseProps}>
            {children}
          </Title>
        }
        placement="bottom"
        trigger={isOverflowing ? 'hover' : ''}
      >
        <Title data-testid={SYMBOL_DISPLAY_SUBHEAD_TEST_ID} ref={ref} {...titleCaseProps}>
          {children}
        </Title>
      </Tooltip>
    </Box>
  ) : (
    <Title data-testid={SYMBOL_DISPLAY_SUBHEAD_TEST_ID} ref={ref} {...titleCaseProps}>
      {children}
    </Title>
  );
};

type SymbolSubheadProps = Pick<
  SymbolDisplayProps,
  | 'multipleSelections'
  | 'security'
  | 'title'
  | 'marketName'
  | 'unifiedLiquidity'
  | 'tooltipOnOverflow'
  | 'selectedMarketAccounts'
  | 'selectedAggregation'
  | 'availableSymbols'
  | 'showNoMarketSelectionWarning'
  | 'onNoMarketSelectionWarningClick'
>;
export const SymbolSubhead = memo((props: SymbolSubheadProps) => {
  const { security, tooltipOnOverflow, selectedMarketAccounts, availableSymbols } = props;
  const { marketAccountsByName } = useMarketAccountsContext();
  const { securitiesBySymbol } = useSecuritiesContext();

  if (!security) {
    return null;
  }

  function buildContent({
    multipleSelections,
    security,
    title,
    marketName,
    unifiedLiquidity,
    selectedAggregation,
    showNoMarketSelectionWarning,
    onNoMarketSelectionWarningClick,
  }: SymbolSubheadProps) {
    if (showNoMarketSelectionWarning) {
      const warningContent = (
        <HStack gap="spacing4" alignItems="center" justifyContent="center">
          <Icon
            icon={IconName.ExclamationSolid}
            color="colorTextWarning"
            data-testid="symbol-display-no-markets-warning-icon"
          />
          <Text as="span" color="colorTextWarning">
            No Markets Selected
          </Text>
          <Icon
            icon={IconName.ExclamationSolid}
            color="colorTextWarning"
            data-testid="symbol-display-no-markets-warning-icon"
          />
        </HStack>
      );

      return onNoMarketSelectionWarningClick ? (
        <NoMarketSelectionWarningButton type="button" onClick={onNoMarketSelectionWarningClick}>
          {warningContent}
        </NoMarketSelectionWarningButton>
      ) : (
        warningContent
      );
    }

    if (title) {
      return title;
    }

    if (!security) {
      return null;
    }

    const productType = security.ProductType;

    let securityMarkets = security?.Markets || [];
    if (
      unifiedLiquidity !== UnifiedLiquidityEnum.Enabled &&
      securityMarkets.length > 1 &&
      (isPerpetualSwap(security) || isFuture(security) || isOption(security) || isETF(security))
    ) {
      const selectedMarket = getFirstSelectedMarketName(selectedMarketAccounts, marketAccountsByName);
      if (selectedMarket) {
        securityMarkets = [selectedMarket];
      }
    }

    if (productType === ProductTypeEnum.Synthetic && unifiedLiquidity === UnifiedLiquidityEnum.Enabled) {
      return (
        <>
          Unified{' '}
          <Expiration>
            {productType === ProductTypeEnum.Synthetic && security?.MultilegDetails?.SyntheticProductType ? (
              <>{mapSyntheticProductTypeToLabel(security.MultilegDetails.SyntheticProductType)}</>
            ) : (
              'Multileg'
            )}
          </Expiration>
        </>
      );
    }

    if (multipleSelections) {
      if (
        (productType === ProductTypeEnum.PerpetualSwap || productType === ProductTypeEnum.CFD) &&
        unifiedLiquidity === UnifiedLiquidityEnum.Enabled
      ) {
        const productTypes = getSymbolsProductTypes(availableSymbols, securitiesBySymbol);
        const expirationLabel = mapProductTypesToLabel(productTypes);

        return (
          <>
            Unified <Expiration>{expirationLabel}</Expiration>
          </>
        );
      }
    }

    // productType === Basis is legacy. Synthetic means it will be a multileg of some sort.
    // Aggregations do not apply to multileg symbols, so handle this before aggregate display logic.
    if (productType === ProductTypeEnum.Basis || productType === ProductTypeEnum.Synthetic) {
      return productType === ProductTypeEnum.Synthetic && security?.MultilegDetails?.SyntheticProductType ? (
        <>{mapSyntheticProductTypeToLabel(security.MultilegDetails.SyntheticProductType)}</>
      ) : (
        'Multileg'
      );
    }

    if (multipleSelections || selectedAggregation != null) {
      // For legal reasons these should be listed as SWAPS
      if (productType === ProductTypeEnum.CFD) {
        return selectedAggregation ? (
          <>
            <SymbolSubheadAggregationIcon /> {selectedAggregation.DisplayName ?? selectedAggregation.Name}{' '}
            <Expiration>SWAP</Expiration>
          </>
        ) : (
          <>
            Aggregate <Expiration>SWAP</Expiration>
          </>
        );
      }

      if (!isPerpetualSwap(security) && !isFuture(security) && !isOption(security)) {
        return selectedAggregation ? (
          <>
            <SymbolSubheadAggregationIcon /> {selectedAggregation.DisplayName ?? selectedAggregation.Name}
          </>
        ) : (
          'Aggregate'
        );
      }
    }

    if (productType === ProductTypeEnum.Spot && marketName) {
      return marketName;
    }

    if (productType === ProductTypeEnum.ExchangeTradedFund) {
      return (
        <>
          <MarketNames markets={securityMarkets} /> <Expiration>ETF</Expiration>
        </>
      );
    }

    if (!productType || isSpot(security) || isCompound(security)) {
      return null;
    }

    if (productType === ProductTypeEnum.CFD) {
      return (
        <>
          <MarketNames markets={securityMarkets} /> <Expiration>SWAP</Expiration>
        </>
      );
    }

    let expiration = 'Perpetual';
    if ((isFuture(security) || isOption(security)) && security.Expiration) {
      expiration = formattedUTCDate(new Date(security.Expiration), '{dd} {Mon} {yy}');
    }

    return (
      <>
        <MarketNames markets={securityMarkets} /> <Expiration>{expiration}</Expiration>{' '}
        {isPerpetualSwap(security) && <PerpTitle security={security} />}
        {isOption(security) && <OptionTitle security={security} />}
      </>
    );
  }

  return (
    <SymbolDisplaySubheadWrapper
      tooltipOnOverflow={tooltipOnOverflow}
      $preserveCase={!!props.showNoMarketSelectionWarning}
    >
      {buildContent(props)}
    </SymbolDisplaySubheadWrapper>
  );
});

function getFirstSelectedMarketName(
  selectedMarketAccounts: string[] | undefined,
  marketAccountsByName: Map<string, MarketAccount>
): string | undefined {
  const selectedMarketAccount = selectedMarketAccounts?.[0];
  return selectedMarketAccount ? marketAccountsByName.get(selectedMarketAccount)?.Market : undefined;
}

function getSymbolsProductTypes(
  symbols: string[] | undefined,
  securitiesBySymbol: Map<string, Security>
): ProductTypeEnum[] {
  if (!symbols || symbols.length === 0) {
    return [];
  }
  return uniq(
    symbols
      .map(symbol => securitiesBySymbol.get(symbol)?.ProductType)
      .filter((productType): productType is ProductTypeEnum => productType !== undefined)
  );
}

function mapProductTypesToLabel(productTypes: ProductTypeEnum[]): string {
  const productTypeLabels: Partial<Record<ProductTypeEnum, string>> = {
    [ProductTypeEnum.PerpetualSwap]:
      productTypes.length > 1 && productTypes.includes(ProductTypeEnum.PerpetualSwap) ? 'Perp' : 'Perpetual',
    [ProductTypeEnum.CFD]: 'Swap',
  };
  return productTypes
    .map(productType => productTypeLabels[productType] || productType)
    .toSorted()
    .join(' & ');
}

const NoMarketSelectionWarningButton = styled.button`
  display: inline-block;
  line-height: inherit;
  vertical-align: top;
  appearance: none;
  padding: 0;
  border: 0;
  margin: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
`;

const SymbolSubheadAggregationIcon = styled(AggregationIcon)`
  line-height: inherit;
  vertical-align: top;
`;
